/**
 * The connection editor's draft, as pure functions. THE PLAN, §4 P8.
 *
 * Same split as `task-draft.js`, for the same reason — a form this long has more state
 * transitions than a mounted test can comfortably reach — plus two things that are unique
 * to this screen and are the reason it is worth its own file:
 *
 *   • **THE BODY IS TEXT IN THE DRAFT AND JSON ON THE WIRE.** The owner types characters;
 *     the sidecar stores a parsed value. Keeping the typed TEXT as the draft's truth is what
 *     lets a half-written `{"area":` stay on screen with "that is not valid JSON yet" under
 *     it, instead of being silently discarded on the keystroke that broke it — which is what
 *     parsing on every input would do.
 *
 *   • **THE SECRET IS WRITE-ONLY AND NEVER ROUND-TRIPS.** It is not in what the server sends
 *     back, so it is not in `wire` either. An empty box means "keep what is stored"; a
 *     filled one is a new value. It therefore counts as a change all by itself, which is why
 *     `connectionPatch` treats it separately from every other field.
 *
 * The dirty check runs against the WIRE shape, so a trailing space, a reformatted body or a
 * row added and removed again all leave the page clean.
 */

/** Row ids are local to one editing session and never leave the browser. */
let nextRowId = 0;
const rowId = () => {
  nextRowId += 1;
  return nextRowId;
};

export const MAX_PARAMS = 16;
export const MAX_KEEP_FIELDS = 16;
export const MAX_DESCRIPTION = 1000;
const MAX_URL = 2000;

/** Exactly the five the sidecar accepts (`http/tools.ts` `baseShape.method`). */
export const CONNECTION_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];

/** The methods that carry something in the body. The others hide that section. */
export const METHODS_WITH_BODY = ['POST', 'PUT', 'PATCH'];

export const CONNECTION_AUTH_TYPES = ['none', 'bearer', 'header', 'basic'];

/** What a connection does (P-3.4). The owner answers this; nothing defaults it. */
export const CONNECTION_PURPOSES = ['lookup', 'change'];

/** The method each answer brings with it, while the method is still the other one's. */
const DEFAULT_METHOD_FOR = { lookup: 'GET', change: 'POST' };

/**
 * What a saved connection does: the BFF's `purpose`, or, from a BFF older than it, what its
 * method has always meant. `null` when there is not even a method to go on.
 */
export const purposeOf = connection => {
  if (CONNECTION_PURPOSES.includes(connection?.purpose)) {
    return connection.purpose;
  }
  if (typeof connection?.method !== 'string' || connection.method === '') {
    return null;
  }
  return connection.method === 'GET' ? 'lookup' : 'change';
};

/** Whether `method` is the one `purpose` brings with it (the Advanced disclosure stays shut). */
export const methodIsDefaultFor = (purpose, method) =>
  CONNECTION_PURPOSES.includes(purpose) &&
  DEFAULT_METHOD_FOR[purpose] === method;

/**
 * The draft with its answer to "What does this do?". The method follows the answer only
 * while it is still the OTHER answer's default: a developer's PUT is never overwritten.
 */
export const withPurpose = (draft, purpose) => {
  if (!CONNECTION_PURPOSES.includes(purpose)) return draft;
  const other = purpose === 'lookup' ? 'change' : 'lookup';
  const method =
    draft.method === DEFAULT_METHOD_FOR[other]
      ? DEFAULT_METHOD_FOR[purpose]
      : draft.method;
  return { ...draft, purpose, method };
};

/**
 * A name or note that reads as WHO the customer is. A copy of the sidecar's
 * `IDENTITY_PARAM` (`services/ai-sidecar/src/tools/http/open-chat-details.ts`), which is
 * the load-bearing one; this one only decides when the page warns.
 */
export const IDENTITY_PARAM =
  /name|email|mail|phone|mobile|tel|whatsapp|اسم|ايميل|بريد|هاتف|جوال|موبايل|رقم/i;

/** P-10.10's connection-page warning: a lookup keyed by a detail the customer types. */
export const looksUpByTypedIdentity = draft =>
  draft?.purpose === 'lookup' &&
  (draft.params ?? []).some(
    row =>
      IDENTITY_PARAM.test(row.name ?? '') ||
      IDENTITY_PARAM.test(row.description ?? '')
  );

export const PARAM_TYPES = ['string', 'number', 'integer', 'boolean'];

/** What the address hint and the details section point at, in both languages. */
export const TOKEN_EXAMPLE = '{{area}}';

export const paramRow = (param = {}) => ({
  id: rowId(),
  name: typeof param.name === 'string' ? param.name : '',
  type: PARAM_TYPES.includes(param.type) ? param.type : 'string',
  description: typeof param.description === 'string' ? param.description : '',
  required: param.required !== false,
});

export const blankDraft = () => ({
  name: '',
  description: '',
  method: 'GET',
  purpose: '',
  url: '',
  enabled: false,
  params: [],
  bodyText: '',
  auth: 'none',
  headerName: '',
  secret: '',
  keepText: '',
});

/** A saved connection, as the form edits it. */
export const toDraft = connection => ({
  name: typeof connection?.name === 'string' ? connection.name : '',
  description:
    typeof connection?.description === 'string' ? connection.description : '',
  method: CONNECTION_METHODS.includes(connection?.method)
    ? connection.method
    : 'GET',
  purpose: purposeOf(connection) ?? '',
  url: typeof connection?.url === 'string' ? connection.url : '',
  enabled: connection?.enabled !== false,
  params: (Array.isArray(connection?.params) ? connection.params : []).map(
    paramRow
  ),
  // Pretty-printed rather than compact: this is the one field somebody has to read and
  // edit by hand, and a one-line object is unreadable past about three keys.
  bodyText:
    connection?.body === null || connection?.body === undefined
      ? ''
      : JSON.stringify(connection.body, null, 2),
  auth: CONNECTION_AUTH_TYPES.includes(connection?.auth?.type)
    ? connection.auth.type
    : 'none',
  headerName:
    typeof connection?.auth?.headerName === 'string'
      ? connection.auth.headerName
      : '',
  // Never populated from the server: there is nothing to populate it from.
  secret: '',
  keepText: Array.isArray(connection?.keepFields)
    ? connection.keepFields.join('\n')
    : '',
});

/**
 * The typed text as a value, or a refusal. Empty is a real answer (`null` = send nothing),
 * not a parse failure — which is why this cannot be `JSON.parse` at the call site.
 */
export const parseBodyText = text => {
  const trimmed = (text ?? '').trim();
  if (trimmed === '') return { ok: true, value: null };
  try {
    return { ok: true, value: JSON.parse(trimmed) };
  } catch {
    return { ok: false, value: null };
  }
};

export const keepFieldsOf = text =>
  (text ?? '')
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean);

/**
 * The draft as the BFF reads it. `name` and `secret` are deliberately absent: the first is
 * create-only (a rename would detach every task attached to it — `routes/api/tools.ts`
 * decision 1), the second is write-only and has no saved value to diff against.
 */
export const draftToWire = draft => {
  const body = parseBodyText(draft.bodyText);
  return {
    description: (draft.description ?? '').trim(),
    method: CONNECTION_METHODS.includes(draft.method) ? draft.method : 'GET',
    purpose: CONNECTION_PURPOSES.includes(draft.purpose) ? draft.purpose : null,
    url: (draft.url ?? '').trim(),
    enabled: draft.enabled !== false,
    params: (draft.params ?? []).map(row => ({
      name: (row.name ?? '').trim(),
      type: PARAM_TYPES.includes(row.type) ? row.type : 'string',
      description: (row.description ?? '').trim(),
      required: row.required !== false,
    })),
    // A body that does not parse is carried as null so the diff is stable; `validateDraft`
    // refuses the save long before this reaches a request.
    body: body.value,
    auth:
      draft.auth === 'header'
        ? { type: 'header', headerName: (draft.headerName ?? '').trim() }
        : {
            type: CONNECTION_AUTH_TYPES.includes(draft.auth)
              ? draft.auth
              : 'none',
          },
    keepFields: keepFieldsOf(draft.keepText),
  };
};

/** Everything a create sends: the wire shape plus the two write-only fields. */
export const draftToCreate = draft => {
  const wire = draftToWire(draft);
  return {
    ...wire,
    name: (draft.name ?? '').trim(),
    ...((draft.secret ?? '') !== '' ? { secret: draft.secret } : {}),
  };
};

const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);

/**
 * Only the keys that actually changed.
 *
 * `body` is compared through `JSON.stringify` of the PARSED value, so reformatting the text
 * — adding a newline, changing the indent — is correctly not a change, while reordering the
 * keys is. That asymmetry is deliberate: the sidecar stores the value, and two values with
 * their keys in a different order are two different stored rows.
 */
export const connectionPatch = (draft, savedWire) => {
  const wire = draftToWire(draft);
  const patch = {};
  if (wire.description !== savedWire.description) {
    patch.description = wire.description;
  }
  if (wire.method !== savedWire.method) patch.method = wire.method;
  if (wire.purpose !== null && wire.purpose !== savedWire.purpose) {
    patch.purpose = wire.purpose;
  }
  if (wire.url !== savedWire.url) patch.url = wire.url;
  if (wire.enabled !== savedWire.enabled) patch.enabled = wire.enabled;
  if (!same(wire.params, savedWire.params)) patch.params = wire.params;
  if (!same(wire.body, savedWire.body)) patch.body = wire.body;
  if (!same(wire.auth, savedWire.auth)) patch.auth = wire.auth;
  if (!same(wire.keepFields, savedWire.keepFields)) {
    patch.keepFields = wire.keepFields;
  }
  // A filled box is a NEW value and is always sent; an empty one keeps what is stored and
  // is never sent. There is nothing to compare it against, so it cannot go through the
  // diff above.
  if ((draft.secret ?? '') !== '') patch.secret = draft.secret;
  return patch;
};

/** The patch keys that change what is SENT. Words, what it keeps and the switch do not. */
const REQUEST_KEYS = ['method', 'url', 'params', 'body', 'auth', 'secret'];

/**
 * Whether a patch changes what the connection sends, which un-proves its last good test.
 *
 * The client's copy of the sidecar's `changesTheRequest` (`http/tools.ts`), which is the one
 * that is load-bearing: a row that is OFF cannot be switched on in the same save as an edit
 * to what it sends, because the test that worked was a test of something else. Asked here so
 * the switch locks before she presses Save, instead of after a refusal. `patch` is
 * `connectionPatch`'s output, whose keys are already only the ones that changed.
 */
export const changesWhatIsSent = patch =>
  Object.keys(patch ?? {}).some(name => REQUEST_KEYS.includes(name));

const NAME_PATTERN = /^[a-z][a-z0-9_]{2,47}$/;
const PARAM_NAME_PATTERN = /^[a-zA-Z_][a-zA-Z0-9_]{0,39}$/;

/** The owner's words → the short name a task attaches by. Mirrors the BFF's slugify. */
export const slugifyName = raw =>
  (raw ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+/, '')
    .replace(/_+$/, '')
    .slice(0, 48);

/**
 * The details WE fill in, from the contact Chatwoot already holds.
 *
 * These are not rows in "Details it fills in" and must never become rows: the assistant is
 * never offered them, which is the entire point. A phone number is the identity of the
 * person messaging, not something said in the conversation — asking the assistant to
 * retype it is how a real booking went out with `"phone": "0"` and the customer got a
 * canned handoff instead of a confirmation.
 *
 * The dot is what makes them safe rather than merely conventional: a declared detail's
 * name cannot contain one (`PARAM_NAME_PATTERN`), so this namespace cannot be shadowed.
 * `services/ai-sidecar/src/tools/http/reserved.ts` is the copy that is load-bearing; this
 * one only decides what the owner is allowed to save.
 */
export const CONTACT_PLACEHOLDERS = [
  'contact.name',
  'contact.email',
  'contact.phone_number',
  'conversation.id',
];

const CONTACT_PLACEHOLDER_SET = new Set(CONTACT_PLACEHOLDERS);

/** Every `{{placeholder}}` in a string. */
const placeholdersIn = text =>
  [...(text ?? '').matchAll(/\{\{\s*([^}]+?)\s*\}\}/g)].map(match => match[1]);

/** Every placeholder anywhere inside a parsed body. */
const placeholdersInBody = value => {
  if (typeof value === 'string') return placeholdersIn(value);
  if (Array.isArray(value)) return value.flatMap(placeholdersInBody);
  if (value && typeof value === 'object') {
    return Object.values(value).flatMap(placeholdersInBody);
  }
  return [];
};

/**
 * True when this connection leans on a detail we fill in from the customer.
 *
 * Read off the SAVED address and body, and used by the dry-run panel only: a test call has
 * no customer behind it, so those details go out blank and the owner's own system is right
 * to refuse. Saying so beside the button is the difference between "my change is broken"
 * and "of course, I have to try that one on Test it".
 */
export const usesContactPlaceholders = (url, body) =>
  [...placeholdersIn(url), ...placeholdersInBody(body)].some(name =>
    CONTACT_PLACEHOLDER_SET.has(name)
  );

/**
 * True when the website part of the address is fixed — no `{{placeholder}}` in the scheme,
 * host or port.
 *
 * This is the one rule on this screen that is a security boundary rather than a usability
 * one, and it is restated here only so the owner reads it under the box instead of after a
 * round trip: a substitutable host means the assistant chooses where her saved sign-in is
 * sent. `routes/api/tools.ts` `hostIsFixed` is the copy that is actually load-bearing.
 */
export const hostIsFixed = url => {
  const text = url ?? '';
  const at = text.indexOf('://');
  if (at === -1) return !text.includes('{{');
  const rest = text.slice(at + 3);
  const end = rest.search(/[/?#]/);
  const authority = end === -1 ? rest : rest.slice(0, end);
  return !authority.includes('{{') && !text.slice(0, at).includes('{{');
};

/**
 * The website an address points at: scheme, host and port, and nothing after them.
 *
 * A PORT OF `hostOf` IN `routes/api/tools.ts`, character for character, because this is
 * the comparison a real refusal is made on and two spellings of it would drift. Not
 * `new URL()`: an address still being typed is not a URL yet, and a half-typed box must
 * not throw on every keystroke.
 */
export const websiteOf = url => {
  const text = url ?? '';
  const at = text.indexOf('://');
  if (at === -1) return text;
  const rest = text.slice(at + 3);
  const end = rest.search(/[/?#]/);
  return `${text.slice(0, at)}://${end === -1 ? rest : rest.slice(0, end)}`;
};

/**
 * Whether a stored sign-in has to be typed again before this draft can be saved.
 *
 * ── THE DEFECT THIS EXISTS TO CLOSE ───────────────────────────────────────────────────
 * The sidecar refuses an edit that moves a connection to a different website while a
 * secret is stored (`http/tools.ts`: "the destination host changed — re-enter this
 * action's secret to save it"), because omitting the secret preserves the stored one and
 * a freely replaceable address would forward a live third-party credential to whatever
 * host somebody typed. The BFF restates it and pins it to the sign-in box
 * (`routes/api/tools.ts`: "The website changed, so enter the sign-in again").
 *
 * The UI said nothing. It kept the hint "A sign-in is already saved. Leave this empty to
 * keep it." — which in this one state is false — and the owner learned otherwise by
 * pressing Save and being refused. A warning for exactly this existed in both languages
 * as `SECRET_HINT_HOST` and was referenced by nothing at all. This is what it is wired to.
 */
export const signInNeedsReentry = (draft, context = {}) => {
  const { isNew = false, signInSaved = false, savedUrl = '' } = context;
  if (isNew || !signInSaved) return false;
  if ((draft?.auth ?? 'none') === 'none') return false;
  if ((draft?.secret ?? '') !== '') return false;
  // NOTHING IS DERIVED FROM AN ABSENCE. A caller that does not know the saved address
  // cannot be told the address has moved — "" is not a website, and treating it as one
  // would refuse every edit made by anybody who did not pass it.
  if (typeof savedUrl !== 'string' || savedUrl === '') return false;
  return websiteOf(draft?.url ?? '') !== websiteOf(savedUrl);
};

/**
 * Every rule `validateConnection` enforces on the server, checked here first so the message
 * lands under the control rather than as a banner after a round trip.
 *
 * @param {object} draft
 * @param {(key: string, params?: object) => string} t
 * @param {{ isNew?: boolean, signInSaved?: boolean, savedUrl?: string }} [context]
 * @returns {Record<string, string>} keyed by form section.
 */
export const validateDraft = (draft, t, context = {}) => {
  const { isNew = false, signInSaved = false } = context;
  const wire = draftToWire(draft);
  const errors = {};
  const key = name => `WASLO.SETTINGS.CONNECTIONS.EDITOR.ERRORS.${name}`;

  if (isNew) {
    const typed = (draft.name ?? '').trim();
    if (!typed) errors.name = t(key('NAME'));
    else if (!NAME_PATTERN.test(slugifyName(typed))) {
      errors.name = t(key('NAME_SHAPE'));
    }
  }

  if (!CONNECTION_PURPOSES.includes(draft.purpose)) {
    errors.purpose = t(key('PURPOSE'));
  }

  if (!wire.description) errors.description = t(key('DESCRIPTION'));
  else if (wire.description.length > MAX_DESCRIPTION) {
    errors.description = t(key('DESCRIPTION_LONG'));
  }

  if (!wire.url) errors.url = t(key('URL'));
  else if (wire.url.length > MAX_URL) errors.url = t(key('URL_LONG'));
  else if (!/^https:\/\//i.test(wire.url)) errors.url = t(key('URL_HTTPS'));
  else if (!hostIsFixed(wire.url)) errors.url = t(key('URL_HOST'));

  const body = parseBodyText(draft.bodyText);
  if (!body.ok) errors.body = t(key('BODY_JSON'));

  if (wire.params.length > MAX_PARAMS) {
    errors.params = t(key('PARAMS_FULL'));
  } else {
    const names = wire.params.map(param => param.name);
    const blank = names.findIndex(name => name === '');
    const shape = names.findIndex(
      name => name !== '' && !PARAM_NAME_PATTERN.test(name)
    );
    if (blank !== -1) {
      errors.params = t(key('PARAM_BLANK'), { number: blank + 1 });
    } else if (shape !== -1) {
      errors.params = t(key('PARAM_SHAPE'), { number: shape + 1 });
    } else if (new Set(names).size !== names.length) {
      errors.params = t(key('PARAM_DUPLICATE'));
    } else if (body.ok) {
      // Only once the body parses: an unparseable body has no placeholders to check, and
      // reporting "nothing tells us what {{x}} is" for text that is not JSON yet sends the
      // owner to fix the wrong box.
      const declared = new Set(names);
      const used = [
        ...placeholdersIn(wire.url),
        ...placeholdersInBody(body.value),
      ];
      const undeclared = used.find(
        name => !declared.has(name) && !CONTACT_PLACEHOLDER_SET.has(name)
      );
      if (undeclared !== undefined) {
        // A dotted name that is not one of ours is a near-miss on a contact detail —
        // `{{contact.phone}}` for `{{contact.phone_number}}` — not a detail the owner
        // forgot to add a row for. Sending her to the Details section for that would be
        // sending her to fix the wrong box.
        errors.params = undeclared.includes('.')
          ? t(key('PARAM_UNKNOWN_CONTACT'), {
              name: undeclared,
              known: CONTACT_PLACEHOLDERS.map(one => `{{${one}}}`).join(', '),
            })
          : t(key('PARAM_UNDECLARED'), { name: undeclared });
      }
    }
  }

  if (wire.auth.type === 'header' && !wire.auth.headerName) {
    errors.auth = t(key('HEADER_NAME'));
  }

  // A sign-in with nothing behind it (P-3.8): the connection may be SAVED without its secret,
  // because it stays off; the secret is needed only to switch it on (a new connection is
  // always created off). On an edit an empty box means "keep what is stored", so it is a
  // problem only when nothing is stored, unless the address has moved to a different
  // website, which is the one case where "keep what is stored" is a thing neither the BFF
  // nor the sidecar will do.
  if (wire.auth.type !== 'none' && (draft.secret ?? '') === '') {
    if (draft.enabled === true && !signInSaved)
      errors.secret = t(key('SECRET'));
    else if (signInNeedsReentry(draft, context)) {
      errors.secret = t(
        'WASLO.SETTINGS.CONNECTIONS.EDITOR.SIGN_IN.SECRET_HINT_HOST'
      );
    }
  }

  if (keepFieldsOf(draft.keepText).length > MAX_KEEP_FIELDS) {
    errors.keepFields = t(key('KEEP_FULL'));
  }

  return errors;
};

export default toDraft;
